import type { StackFrame } from '@outpost/api-client';

/**
 * Whether a stack trace is worth a nudge to upload source maps: some frame
 * looks like bundle output that no map was applied to.
 *
 * `symbolicated` is omitted from the payload unless it is true, so an absent
 * flag means "not mapped". Comparing against `false` would never match what the
 * server actually sends.
 */
export function hasUnmappedMinifiedFrame(frames: StackFrame[]): boolean {
  return frames.some((frame) => !frame.symbolicated && looksMinified(frame));
}

/**
 * Heuristic for "this frame looks like unmapped bundle output".
 *
 * Used only to decide whether to nudge the user about source maps, so a false
 * positive costs a banner, not correctness.
 */
export function looksMinified(frame: StackFrame): boolean {
  const path = frame.abs_path ?? frame.filename ?? '';
  if (!/\.(js|mjs|cjs)$/.test(path)) return false;
  // Bundlers emit everything on very few lines with huge column numbers.
  return (frame.colno ?? 0) > 200 || /\.[0-9a-f]{6,}\.js$/.test(path);
}
